import { PAYLOAD_DATE_FORMAT } from '../constants/date';
import { formatDateWithString, getApiCalendarDate } from './date';

const UPCOMING_STATUSES = ['PENDING', 'SCHEDULED'];
const CALENDAR_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/;

export const PROMISE_ADD_PAYMENT_METHOD_FLOW = 'promiseAddPaymentMethod';

export const getUpcomingPromisePayments = (paymentSchedule = []) =>
  paymentSchedule.filter((payment) => {
    if (!payment?.paymentStatus) return true;
    return UPCOMING_STATUSES.includes(payment.paymentStatus);
  });

export const getPromisePaymentProgress = (
  remainingPaymentCount,
  completedPaymentCount
) => {
  const remaining = Math.max(0, Number(remainingPaymentCount) || 0);
  const completed = Math.max(0, Number(completedPaymentCount) || 0);
  const total = remaining + completed;

  return {
    completedPaymentCount: completed,
    totalPaymentCount: total,
    progressPercent: total > 0 ? Math.min((completed / total) * 100, 100) : 0,
  };
};

const TRUE_VALUES = new Set(['true', '1', 'yes', 'y']);
const FALSE_VALUES = new Set(['false', '0', 'no', 'n', 'none', 'null', 'undefined']);

const hasMeaningfulValue = (value) => {
  if (value === true) return true;
  if (value === false || value == null) return false;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized || FALSE_VALUES.has(normalized)) return false;
    if (TRUE_VALUES.has(normalized)) return true;
    return true;
  }
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (typeof value === 'object') {
    return Object.values(value).some(hasMeaningfulValue);
  }

  return Boolean(value);
};

export const hasAssociatedPaymentMethod = (plan) =>
  [
    plan?.hasPaymentMethod,
    plan?.paymentMethodAssociated,
    plan?.paymentMethod,
    plan?.paymentMethodId,
    plan?.paymentInstrumentId,
  ].some(hasMeaningfulValue);

export const isPromisePlan = (plan) => {
  if (!plan) return false;
  if (typeof plan.isPromise === 'boolean') return plan.isPromise;

  return Boolean(plan.paymentSchedule?.length && !hasAssociatedPaymentMethod(plan));
};

export const isPromiseFullBalance = (promisePlan) => {
  const upcoming = getUpcomingPromisePayments(promisePlan?.paymentSchedule);
  if (upcoming.length !== 1) return false;

  const paymentAmount = Number(upcoming[0]?.paymentAmount);
  const remainingBalance = Number(promisePlan?.remainingBalance);

  if (!Number.isFinite(paymentAmount) || !Number.isFinite(remainingBalance)) {
    return false;
  }

  return paymentAmount >= remainingBalance - 0.01;
};

export const isPromiseAddPaymentMethodFlow = (paymentPlan, isPromise) =>
  Boolean(isPromise && paymentPlan?.flow === PROMISE_ADD_PAYMENT_METHOD_FLOW);

const toCalendarDateKey = (value) => {
  const match = CALENDAR_DATE_PREFIX.exec(String(value ?? '').trim());
  if (match) return match[1];

  const date = getApiCalendarDate(value);
  return date ? formatDateWithString(date, PAYLOAD_DATE_FORMAT) : null;
};

// The payment-plans API does not guarantee date order or that paid rows are
// excluded, so rows are filtered to upcoming and ordered by calendar date.
const getRemainingPromiseRows = (promisePlan) => {
  const upcoming = getUpcomingPromisePayments(promisePlan.paymentSchedule)
    .flatMap((payment) => {
      const dateKey = toCalendarDateKey(payment?.paymentDate);
      return dateKey ? [{ payment, dateKey }] : [];
    })
    .sort((a, b) => (a.dateKey === b.dateKey ? 0 : a.dateKey < b.dateKey ? -1 : 1));

  const nextDateKey = toCalendarDateKey(promisePlan.nextPaymentDate);
  const fromNext = nextDateKey
    ? upcoming.filter(({ dateKey }) => dateKey >= nextDateKey)
    : upcoming;

  return fromNext.length ? fromNext : upcoming;
};

export const hydratePaymentPlanFromPromise = (promisePlan, referenceNumber) => {
  if (!promisePlan?.paymentSchedule?.length) {
    return null;
  }

  const remainingRows = getRemainingPromiseRows(promisePlan);
  if (!remainingRows.length) {
    return null;
  }

  const remainingPromiseSchedule = remainingRows.map(
    ({ payment: { paymentAmount, paymentDate } }) => ({
      amount: paymentAmount,
      date: paymentDate,
    })
  );

  return {
    planType: null,
    paymentFrequency: null,
    paymentDayOfWeek: null,
    standardPaymentAmount: null,
    // UTC midnight of the calendar date: the confirm page reads it with UTC
    // getters and the Repay charge-now decision reads UTC midnight as that day.
    paymentStartDate: new Date(`${remainingRows[0].dateKey}T00:00:00.000Z`),
    paymentSchedule: remainingPromiseSchedule,
    numPayments: remainingPromiseSchedule.length,
    referenceNumber,
    totalPaymentAmount: promisePlan.remainingBalance,
    promisePlanId: promisePlan.planId,
    flow: PROMISE_ADD_PAYMENT_METHOD_FLOW,
  };
};
